import { AgoraRemoteUsersInterface } from "@share/web-conferences/interfaces/AgoraRemoteUsersInterface";
import {IAgoraRTCClient, IAgoraRTCRemoteUser} from "agora-rtc-sdk-ng";
import {AgoraUserEvents} from "@share/web-conferences/enums/AgoraUserEvents";
import {AgoraMediaType} from "@share/web-conferences/types/AgoraMediaType";
import {TracksManagerInterface} from "@share/web-conferences/interfaces/TracksManagerInterface";
import {toUserTracks} from "@share/web-conferences/mappers/AgoraRemoteUserToUserTracksMapper";
import {UserProfilesService} from "@share/web-conferences/data/UserProfilesService";
import {getUserIdFromUid} from "@share/web-conferences/helpers/UserIdHelper";
import {Store} from "vuex";
import {addJoinedUserAction, addPresenterAction, removeJoinedUserAction} from "@share/web-conferences/store/actions";
import {WebConferenceService} from "@share/web-conferences/data/WebConferenceService";
import {isShareScreen} from "@share/web-conferences/helpers/IsShareScreen";
import {SpeakerChecker} from "@share/web-conferences/helpers/SpeakerChecker";
import {runTimeoutForCurrentPresentation} from "@share/web-conferences/helpers/slides/CurrentPresentationAbsenceHandler";
import {StoreHelper} from "@share/web-conferences/helpers/StoreHelper";
import { AddPresentersParam } from "../types/AddPresentersParam";
import { stateKey } from "../helpers/StateKey";
import { UserProfile } from "@api/models/UserProfile";


export class AgoraRemoteUsers implements AgoraRemoteUsersInterface {
    private client: IAgoraRTCClient;
    public tracksManager: TracksManagerInterface;

    constructor(
        private readonly userProfilesService: UserProfilesService,
        private readonly speakerChecker: SpeakerChecker,
        private readonly storeHelper: StoreHelper,
        private readonly webConferenceService: WebConferenceService
    ) {
    }

    private offAllHandlers() {
        this.client.removeAllListeners(AgoraUserEvents.UserJoined);
        this.client.removeAllListeners(AgoraUserEvents.UserLeft);
        this.client.removeAllListeners(AgoraUserEvents.UserPublished);
        this.client.removeAllListeners(AgoraUserEvents.UserUnpublished);
    }

    async subscribe(client: IAgoraRTCClient, tracksManager: TracksManagerInterface): Promise<void> {
        this.client = client;
        this.tracksManager = tracksManager;

        this.offAllHandlers();

        this.client.on(AgoraUserEvents.UserJoined, this.handleUserJoined.bind(this));
        this.client.on(AgoraUserEvents.UserLeft, this.handleUserLeft.bind(this));

        this.client.on(AgoraUserEvents.UserPublished, this.handlePublished.bind(this));
        this.client.on(AgoraUserEvents.UserUnpublished, this.handleUnpublished.bind(this));
    }

    private skipUser(user: IAgoraRTCRemoteUser) {
        return this.webConferenceService?.agoraCredentials?.screenSharingUserId == user?.uid ||
               this.webConferenceService?.agoraCredentials?.userId == user?.uid;
    }

    /**
     * Before subscribing to published track it is needed to be checked whether the uid exists and is a presenter in our system
     * @param user
     * @param mediaType
     */
    async handlePublished(user: IAgoraRTCRemoteUser, mediaType: AgoraMediaType): Promise<void> {
        console.log('user published', user, mediaType);

        if (this.skipUser(user)) {
            return;
        }

        const userId = getUserIdFromUid(user.uid);
        if (this.speakerChecker.isSpeaker(userId)) {
            this.userProfilesService.fetchProfile(userId);

            try {
                await this.client.subscribe(user, mediaType);
            }
            catch (e) {
                this.storeHelper.store.dispatch('reportException', e);
            }

            try {
                await this.tracksManager.update(toUserTracks(user), mediaType);
            }
            catch (e) {
                this.storeHelper.store.dispatch('reportException', e);
            }
        }
    }

    /**
     * Before adding the user to our list it is needed to be checked whether the uid exists and is a presenter in our system
     * @param user
     * @param mediaType
     */
    async handleUnpublished(user: IAgoraRTCRemoteUser, mediaType: AgoraMediaType): Promise<void> {
        console.log('user unpublished', user, mediaType);

        if (this.skipUser(user)) {
            return;
        }

        await this.tracksManager.update(toUserTracks(user), mediaType);
    }

    async handleUserJoined(user: IAgoraRTCRemoteUser): Promise<void> {
        console.log('user joined', user);
        if (this.skipUser(user) || isShareScreen(user.uid.toString())) {
            return;
        }

        const userId = getUserIdFromUid(user.uid);
        if (this.speakerChecker.isSpeaker(userId)) {
            this.userProfilesService.fetchProfile(userId)
                .then((profile) => {
                    const key = stateKey(this.webConferenceService.schemaId, this.webConferenceService.objectId);

                    const alreadyHas = !!(this.storeHelper.store.state.webConference.presenters[key] as UserProfile[] | undefined)
                        ?.find((presenter: UserProfile) => presenter.userId === profile?.userId);

                    if (!alreadyHas) {
                        this.storeHelper.store.dispatch(addPresenterAction, {
                            key: key,
                            user: profile
                        } as AddPresentersParam);
                    }
                })

            try {
                await this.tracksManager.update(toUserTracks(user), null);
            }
            catch (e) {
                this.storeHelper.store.dispatch('reportException', e);
            }
        }

        this.storeHelper.addJoinedUser(getUserIdFromUid(user.uid));
    }

    async handleUserLeft(user: IAgoraRTCRemoteUser, reason: string) {
        console.log('user left', user);
        console.log('user left reason', reason);

        if (this.skipUser(user)) {
            return;
        }

        try {
            await this.tracksManager.remove(user.uid);
        }
        catch (e) {
            this.storeHelper.store.dispatch('reportException', e);
        }

        if (!isShareScreen(user.uid.toString())) {
            const userId = getUserIdFromUid(user.uid);
            this.storeHelper.removeJoinedUser(userId);

            if (this.webConferenceService?.webConferenceData?.isSharingUser(userId)) {
                runTimeoutForCurrentPresentation(this.webConferenceService, this.storeHelper.store);
            }
        }
    }


    clean(): void {
        console.log('clean');
    }
}
